import { getApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, deleteToken, isSupported, type Messaging } from 'firebase/messaging'
import { sessionsApi, tokenManager } from '@cravo/api-service'
import { getDeviceId as getAuthDeviceId } from '@cravo/auth'
import toast from 'react-hot-toast'
import { notifyNewOrders, playNewOrderSound } from './order-alerter'

function clearLocalFcmState(): void {
  if (typeof localStorage === 'undefined') return
  localStorage.removeItem(FCM_TOKEN_KEY)
  localStorage.removeItem(FCM_REGISTERED_USER_KEY)
}

function isSessionMissingError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { code?: string; statusCode?: number }
  return e.code === 'SESSION_NOT_FOUND' || e.statusCode === 404
}

const FCM_TOKEN_KEY = 'cravo-fcm-token'
const FCM_REGISTERED_USER_KEY = 'cravo-fcm-registered-user'
// Legacy key — earlier builds stored a separate device id here. We migrate
// off of it and unify with auth-provider's `cravo-device-id` so the session
// created at login matches the deviceId used by FCM register/deregister.
const LEGACY_DEVICE_ID_KEY = 'cravo-web-device-id'

let messagingInstance: Messaging | null = null
let foregroundUnsubscribe: (() => void) | null = null

/**
 * Single source of truth for this browser's device id — matches the value
 * sent on socialLogin / verifyOtp so the backend session lookup by
 * (userId, deviceId) succeeds when registering an FCM token.
 *
 * One-time migration: if a legacy `cravo-web-device-id` is present we
 * delete it so we don't keep two ids around.
 */
export function getOrCreateDeviceId(): string {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(LEGACY_DEVICE_ID_KEY)
  }
  return getAuthDeviceId()
}

async function getMessagingInstance(): Promise<Messaging | null> {
  if (messagingInstance) return messagingInstance
  try {
    const supported = await isSupported()
    if (!supported) return null
    messagingInstance = getMessaging(getApp())
    return messagingInstance
  } catch {
    return null
  }
}

/**
 * Build the camelCase Firebase config that the SW (`firebase.initializeApp`)
 * expects, from the snake_case shape published in
 * `__TENANT_CONFIG__.firebase` by the integrations endpoint. Returns null
 * when the tenant hasn't configured Firebase (rare — only Cravo standalone
 * deploys without per-tenant Firebase).
 */
function buildSwFirebaseConfig(): {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
} | null {
  if (typeof window === 'undefined') return null
  const fb = window.__TENANT_CONFIG__?.firebase as
    | {
        api_key?: string; apiKey?: string
        auth_domain?: string; authDomain?: string
        project_id?: string; projectId?: string
        storage_bucket?: string; storageBucket?: string
        messaging_sender_id?: string; messagingSenderId?: string
        app_id?: string; appId?: string
      }
    | undefined
  if (!fb) return null
  const apiKey = fb.apiKey ?? fb.api_key
  const projectId = fb.projectId ?? fb.project_id
  if (!apiKey || !projectId) return null
  return {
    apiKey,
    authDomain: fb.authDomain ?? fb.auth_domain ?? '',
    projectId,
    storageBucket: fb.storageBucket ?? fb.storage_bucket ?? '',
    messagingSenderId: fb.messagingSenderId ?? fb.messaging_sender_id ?? '',
    appId: fb.appId ?? fb.app_id ?? '',
  }
}

/**
 * Post the tenant Firebase config to whichever SW state is reachable.
 *
 * The SW lifecycle has installing → waiting → active. On first registration
 * `reg.active` is null until activation completes; only `reg.installing` is
 * defined. Earlier code only tried `reg.active` and silently dropped the
 * config — which meant the SW never received the tenant Firebase config
 * on its first install. Background pushes then ran against whatever default
 * the SW had baked in (Cravo's project, since removed), and Google FCM
 * responded `401 Unauthorized` from
 * fcmregistrations.googleapis.com/v1/projects/<tenant>/registrations.
 */
function postFirebaseConfigToSw(reg: ServiceWorkerRegistration, config: object): void {
  const targets = [reg.active, reg.waiting, reg.installing].filter(
    (w): w is ServiceWorker => w !== null,
  )
  for (const worker of targets) {
    try {
      worker.postMessage({ type: 'FIREBASE_CONFIG', config })
    } catch {
      // Stale worker reference — safe to ignore; the next reachable worker
      // (or the post-activation retry below) will get the message.
    }
  }
}

/**
 * Build the {name, logoUrl} pair for the SW notification fallback. Pulls
 * from the runtime-injected __TENANT_CONFIG__ (tenant name + square or
 * primary logo). Returns null if neither is available so we don't post an
 * empty payload that would just clobber a previous good value.
 */
function buildTenantBrandPayload():
  | { name?: string; logoUrl?: string }
  | null {
  if (typeof window === 'undefined') return null
  const cfg = window.__TENANT_CONFIG__ as
    | {
        tenant?: { name?: string }
        branding?: {
          name?: string
          logos?: { square?: string | null; primary?: string | null }
        }
      }
    | undefined
  if (!cfg) return null
  const name = cfg.branding?.name ?? cfg.tenant?.name ?? undefined
  const logos = cfg.branding?.logos
  const logoUrl = logos?.square ?? logos?.primary ?? undefined
  if (!name && !logoUrl) return null
  return { name, logoUrl }
}

/**
 * Push the tenant's name + logo into every SW that controls this origin.
 * Two SWs may be live at once: the FCM-scoped `firebase-messaging-sw.js`
 * (registered explicitly above) and the workbox-generated `sw.js` (from
 * VitePWA) which now imports `sw-extra-handlers.js` to handle pushes.
 * Both listen for `SET_TENANT_BRANDING`, so we broadcast to both.
 */
async function postTenantBrandingToAllSws(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  const brand = buildTenantBrandPayload()
  if (!brand) return
  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    for (const reg of registrations) {
      const targets = [reg.active, reg.waiting, reg.installing].filter(
        (w): w is ServiceWorker => w !== null,
      )
      for (const worker of targets) {
        try {
          worker.postMessage({ type: 'SET_TENANT_BRANDING', brand })
        } catch { /* ignore */ }
      }
    }
  } catch {
    // No-op if browser blocks getRegistrations — fallback in the SW
    // (self.__TENANT_CONFIG__ check) still covers most cases.
  }
}

async function getFcmServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined
  try {
    const existing = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
    const reg =
      existing ??
      (await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope',
      }))

    const swConfig = buildSwFirebaseConfig()
    if (swConfig) {
      // First-pass: post to whatever SW state exists right now.
      postFirebaseConfigToSw(reg, swConfig)
      // Second-pass: re-post once the worker activates, in case the first
      // pass landed on `installing` (which can't receive messages yet on
      // some browsers) or `controller` swapped during navigation.
      if (reg.installing) {
        reg.installing.addEventListener('statechange', (e) => {
          const w = e.target as ServiceWorker
          if (w.state === 'activated') postFirebaseConfigToSw(reg, swConfig)
        })
      }
    }

    return reg
  } catch {
    return undefined
  }
}

export type RegisterPushResult =
  | { ok: true; token: string }
  | { ok: false; reason: 'unsupported' | 'permission-denied' | 'no-vapid-key' | 'token-failed' | 'register-failed' }

/**
 * Resolve the Firebase Web Push VAPID key for the current tenant.
 *
 * Each LabelGenic tenant has its own Firebase project with its own VAPID
 * key, written into `integrations_json.firebase.vapid_key` from the admin
 * dashboard and hoisted onto `__TENANT_CONFIG__.firebase.vapid_key` by the
 * integrations bootstrap. Read lazily on every call so a captured-at-
 * import-time const doesn't lock in an empty string (the bootstrap
 * resolves AFTER this module first loads).
 *
 * Returns '' when no tenant VAPID is available — caller treats that as
 * `no-vapid-key` and skips registration. NO build-time env var fallback:
 * VITE_FIREBASE_VAPID_KEY would belong to Cravo's Firebase project, and
 * mixing it with a tenant's main-thread Firebase init triggers the same
 * cross-project 401 from FCM we just spent a day fixing.
 */
function resolveVapidKey(): string {
  if (typeof window === 'undefined') return ''
  const fb = (window.__TENANT_CONFIG__ as { firebase?: { vapid_key?: string } } | undefined)?.firebase
  return fb?.vapid_key ?? ''
}

export async function registerPushNotifications(userId: string): Promise<RegisterPushResult> {
  const vapidKey = resolveVapidKey()
  if (!vapidKey) return { ok: false, reason: 'no-vapid-key' }

  const messaging = await getMessagingInstance()
  if (!messaging) return { ok: false, reason: 'unsupported' }

  if (typeof Notification === 'undefined') return { ok: false, reason: 'unsupported' }
  if (Notification.permission === 'denied') return { ok: false, reason: 'permission-denied' }
  if (Notification.permission !== 'granted') {
    const result = await Notification.requestPermission()
    if (result !== 'granted') return { ok: false, reason: 'permission-denied' }
  }

  const swRegistration = await getFcmServiceWorkerRegistration()
  void postTenantBrandingToAllSws()

  let token: string
  try {
    token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    })
  } catch {
    return { ok: false, reason: 'token-failed' }
  }
  if (!token) return { ok: false, reason: 'token-failed' }

  // Mobile parity: 3-attempt retry with exponential backoff on transient
  // network failures (push_notification_service.dart:755 retries up to 3x).
  let lastError: unknown = null
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await sessionsApi.registerFcmToken({
        token,
        platform: 'web',
        deviceId: getOrCreateDeviceId(),
      })
      lastError = null
      break
    } catch (error) {
      lastError = error
      // Backend session got recycled (logout/expiry). No point retrying —
      // clear local state so we don't keep replaying the stale token.
      if (isSessionMissingError(error)) {
        clearLocalFcmState()
        return { ok: false, reason: 'register-failed' }
      }
      // Wait then retry: 500ms, 1500ms (exponential-ish)
      if (attempt < 2) {
        await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1) * (attempt + 1)))
      }
    }
  }
  if (lastError) {
    return { ok: false, reason: 'register-failed' }
  }

  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(FCM_TOKEN_KEY, token)
    localStorage.setItem(FCM_REGISTERED_USER_KEY, userId)
  }

  attachForegroundListener()

  return { ok: true, token }
}

/**
 * Listen for foreground messages — fires only while the app tab is
 * focused. We surface them as a toast and trigger the existing in-app
 * alert pipeline (sound + title flash if hidden).
 */
function attachForegroundListener() {
  if (foregroundUnsubscribe) return
  void getMessagingInstance().then((messaging) => {
    if (!messaging) return
    foregroundUnsubscribe = onMessage(messaging, (payload) => {
      const title = payload.notification?.title ?? payload.data?.title ?? 'New notification'
      const body = payload.notification?.body ?? payload.data?.body ?? ''
      const type = payload.data?.type

      if (type === 'VENDOR_NEW_ORDER') {
        // Existing alerter handles sound + title flash + browser notification
        notifyNewOrders(1)
      } else {
        playNewOrderSound()
      }

      toast(`${title}${body ? `: ${body}` : ''}`, {
        icon: '🔔',
        duration: 6000,
      })
    })
  })
}

export async function deregisterPushNotifications(): Promise<void> {
  const messaging = await getMessagingInstance()
  if (!messaging) return

  const storedToken = typeof localStorage !== 'undefined' ? localStorage.getItem(FCM_TOKEN_KEY) : null

  // Only attempt to call the backend if we still have an auth session — calling
  // /sessions/* without a JWT is guaranteed to fail and will surface noise
  // (401 → refresh attempt → "Unable to connect" toast). Local cleanup below
  // is enough; backend session is already gone after logout.
  if (storedToken && tokenManager.hasTokens()) {
    try {
      await sessionsApi.deregisterFcmToken({ token: storedToken, platform: 'web' })
    } catch {
      // Non-fatal — token may already be invalid on backend
    }
  }

  try {
    await deleteToken(messaging)
  } catch {
    // Non-fatal
  }

  clearLocalFcmState()

  if (foregroundUnsubscribe) {
    foregroundUnsubscribe()
    foregroundUnsubscribe = null
  }
}

export function isPushRegisteredForUser(userId: string): boolean {
  if (typeof localStorage === 'undefined') return false
  return (
    localStorage.getItem(FCM_REGISTERED_USER_KEY) === userId &&
    Boolean(localStorage.getItem(FCM_TOKEN_KEY))
  )
}

/**
 * Best-effort early-attach of foreground listener if user already has a
 * token. Called once at app start so we don't miss messages during the
 * window between auth bootstrap and explicit register call.
 */
export async function bootstrapPushIfRegistered(): Promise<void> {
  if (typeof localStorage === 'undefined') return
  // Bail if no auth tokens — re-registering FCM without a session is
  // guaranteed to 401/SESSION_NOT_FOUND and just adds noise.
  if (!tokenManager.hasTokens()) return
  const stored = localStorage.getItem(FCM_TOKEN_KEY)
  if (!stored) return
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  attachForegroundListener()
  void postTenantBrandingToAllSws()

  // Try to restore service worker registration silently (browsers may have
  // cleared it). If getToken yields a different token, persist it.
  try {
    const vapidKey = resolveVapidKey()
    if (!vapidKey) return
    const messaging = await getMessagingInstance()
    if (!messaging) return
    const swRegistration = await getFcmServiceWorkerRegistration()
    const fresh = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration })
    if (fresh && fresh !== stored) {
      localStorage.setItem(FCM_TOKEN_KEY, fresh)
      try {
        await sessionsApi.registerFcmToken({
          token: fresh,
          platform: 'web',
          deviceId: getOrCreateDeviceId(),
        })
      } catch (error) {
        if (isSessionMissingError(error)) clearLocalFcmState()
      }
    }
  } catch {
    // Silent — bootstrap path must not throw on reload
  }
}
